import { electronicFormatIBAN, isValidBIC, isValidIBAN } from 'ibantools';
import { isValidPhoneNumber, parsePhoneNumberWithError } from 'libphonenumber-js';
import { type ZodCoercedString as ZodCoercedStringBase, type core, z as zod } from 'zod';

import { AwpMarineId, type AwpMarineObjectType } from './ids';

// the registry's `get` also reads the schema a check such as `.min()` copied, so the mark survives checks
const sensitiveRegistry = zod.registry<{ sensitive: true }>();

function markSensitive<T extends core.$ZodType>(schema: T): T {
  sensitiveRegistry.add(schema, { sensitive: true });
  return schema;
}

// we are doing this to allow use modify zod to our taste
export const z = {
  ...zod,

  /**
   * Schemas for values never stored outside the record they belong to, such as in the request log, and whose readers
   * are logged. Checks and wrappers keep the mark: `z.sensitive.string().min(1).nullable()`.
   */
  sensitive: {
    string: (params?: string | core.$ZodStringParams) => markSensitive(zod.string(params)),
    url: (params?: string | core.$ZodURLParams) => markSensitive(zod.url(params)),
    iban: (error?: string | (() => string)) => markSensitive(z.iban(error)),
    /** A whole record of answers or details, redacted as one value. */
    record: <Key extends core.$ZodRecordKey, Value extends core.SomeType>(
      keyType: Key,
      valueType: Value,
      params?: string | core.$ZodRecordParams,
    ) => markSensitive(zod.record(keyType, valueType, params)),
  },

  /**
   * Schema for date-times that cross a JSON boundary (e.g. `jsonb` columns), where serialization
   * turns `Date`s into ISO 8601 strings - accepts either and always outputs a `Date`.
   */
  datetime: (params: core.$ZodISODateTimeParams = { offset: true }) =>
    zod
      .date(params)
      .or(zod.iso.datetime(params))
      .transform((value) => (value instanceof Date ? value : new Date(value))),

  /** Schema for validating passwords with complexity requirements. */
  password: (params?: string | core.$ZodStringParams) =>
    zod
      .string(params)
      .min(8, 'Password must be at least 8 characters long')
      .max(32, 'Password must be at most 32 characters long')
      .refine((value) => /[A-Z]/.test(value), 'Password must contain at least one uppercase letter')
      .refine((value) => /[a-z]/.test(value), 'Password must contain at least one lowercase letter')
      .refine((value) => /[0-9]/.test(value), 'Password must contain at least one number')
      .refine((value) => /[!@#$%^&*(),.?":{}|<>]/.test(value), 'Password must contain at least one special character'),

  /** Schema for `yyyy-MM` month strings (calendar URLs, month inputs). */
  month: (params?: string | core.$ZodStringParams) =>
    zod.string(params).regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Invalid year-month'),

  /** Schemas for country codes. */
  countries: {
    /** Schema for ISO 3166-1 alpha-2 country codes. */
    alpha2: (params?: string | core.$ZodStringParams) =>
      zod.string(params).regex(/^[A-Z]{2}$/, 'Invalid ISO 3166-1 alpha-2 country code'),

    /** Schema for ISO 3166-1 alpha-3 country codes. */
    alpha3: (params?: string | core.$ZodStringParams) =>
      zod.string(params).regex(/^[A-Z]{3}$/, 'Invalid ISO 3166-1 alpha-3 country code'),

    /** Schema for either ISO 3166-1 alpha-2 or alpha-3 country codes. */
    code: (params?: string | core.$ZodStringParams) =>
      zod.string(params).regex(/^[A-Z]{2,3}$/, 'Invalid country code (must be 2 or 3 uppercase letters)'),
  },

  /** Schema for ISO 4217 currency codes. */
  currency: (params?: string | core.$ZodStringParams) =>
    zod.string(params).regex(/^[A-Z]{3}$/, 'Invalid ISO 4217 currency code'),

  /** Schema for money values. */
  money: () =>
    zod.object(
      {
        /** ISO 4217 currency code (e.g., `GBP`). */
        currency: z.currency(),

        /** Amount in the smallest currency unit (e.g., pennies for GBP). */
        amount: zod.number().int(),
      },
      'Invalid money',
    ),

  /** Schema for date periods with start and end dates. */
  period: () =>
    zod.object(
      {
        /** Start date of the period. */
        start: zod.coerce.date(),

        /** End date of the period. */
        end: zod.coerce.date(),
      },
      'Invalid period',
    ),

  /**
   * Schema for phone numbers, output in E.164 format.
   *
   * `isValidPhoneNumber` accepts a valid number in any formatting, so the value is reformatted
   * rather than passed through - otherwise `+44 7703 534208` and `+447703534208` reach the database
   * as two different values for one number. `error` is the message for anything that is not a valid
   * number; a function is read when validation runs, so a translated form can pass one.
   */
  phoneNumber: (error: string | (() => string) = 'Invalid phone number') =>
    zod
      .string({ error })
      .refine((value) => isValidPhoneNumber(value), { error })
      .transform((value) => parsePhoneNumberWithError(value).format('E.164')),

  /**
   * Schema for IBANs, output without spaces and in capitals, so one account reaches the database as one value.
   * The check digits and the length registered for its country are checked.
   */
  iban: (error: string | (() => string) = 'Invalid IBAN') =>
    zod
      .string({ error })
      .transform((value) => electronicFormatIBAN(value) ?? '')
      .refine((value) => isValidIBAN(value), { error }),

  /** Schema for SWIFT (BIC) codes of 8 or 11 characters, output without spaces and in capitals. */
  bic: (error: string | (() => string) = 'Invalid SWIFT or BIC code') =>
    zod
      .string({ error })
      .transform((value) => value.replace(/\s/g, '').toUpperCase())
      .refine((value) => isValidBIC(value), { error }),

  /** Schema for Vessel IMO numbers, which must be exactly 7 digits with valid checksum. */
  imo: (params?: string | core.$ZodStringParams) =>
    zod
      .string(params)
      .regex(/^\d{7}$/, 'IMO must be exactly 7 digits')
      .refine((value) => {
        // Validate IMO checksum using Luhn mod-10 algorithm
        const digits = value.split('').map(Number);
        const checkDigit = digits[6];
        let sum = 0;
        for (let i = 0; i < 6; i++) {
          sum += digits[i]! * (7 - i);
        }
        const calculatedCheckDigit = sum % 10;
        return calculatedCheckDigit === checkDigit;
      }, 'Invalid IMO checksum'),

  /** Schema for search query parameters. */
  searchQuery: () =>
    zod.object({
      q: zod.string().trim().max(100).default(''),
      limit: zod.coerce.number().int().min(1).max(50).default(20),
      selectedId: z.singleOrArray(zod.string().trim()).optional(),
    }),

  /**
   * Schema for UN/LOCODE (United Nations Code for Trade and Transport Locations).
   * 5 characters: 2-letter country code + 3 alphanumeric location code.
   */
  unlocode: (params?: string | core.$ZodStringParams) =>
    zod
      .string(params)
      .regex(
        /^[A-Z]{2}[A-Z0-9]{3}$/,
        'Invalid UN/LOCODE (must be 2-letter ISO country code followed by 3 alphanumeric characters)',
      ),

  /** Schema for slugs - lowercase alphanumeric with hyphens, max 50 chars. */
  slug: (params?: string | core.$ZodStringParams) =>
    zod
      .string(params)
      .max(50, 'Slug must be at most 50 characters')
      .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'Slug can only contain lowercase letters, numbers, and hyphens'),

  invoicePrefix: (params?: string | core.$ZodStringParams) =>
    zod
      .string(params)
      .min(4, 'Invoice prefix must be at least 4 characters')
      .max(6, 'Invoice prefix must be at most 6 characters')
      .transform((value) => value.toUpperCase())
      .refine((value) => /^[A-Z0-9]+$/.test(value), 'Invoice prefix can only contain uppercase letters and numbers'),

  /** Schemas for links RightShip sends us. */
  rightship: {
    /**
     * A link from a RightShip email, such as where to submit or decline a proposal, for example
     * `https://u23226779.ct.sendgrid.net/ls/click?upn=...`. Only https, since the dashboard renders these as links. The
     * host is left open: the emails send every link through SendGrid click tracking, whose host has changed before.
     */
    emailedLink: (error: string | (() => string) = 'Invalid URL format') => zod.url({ error, protocol: /^https$/ }),

    /**
     * A RightShip inspection report, for example
     * `https://mobileinspections.rightship.com/rbr/41795726/0/1632993863/3921/?Dir=&Enc_Dir=204742a1&signature=f6398be4865abdfef713b10294b2ad80`
     * or
     * `https://mobileinspections.rightship.com/reportmodule/REPORT2/rbr/41795726/0/1617998465/1278/?Dir=&Enc_Dir=204742a1&signature=298c7c9fcda724937b1b765a36ca5443&msig=cd7886175f9adfceb02bd67426535125`.
     */
    reportUrl: (error: string | (() => string) = 'Invalid URL format') =>
      zod.url({
        error,
        protocol: /^https?$/,
        hostname: /mobileinspections\.rightship\.com$/,
      }),
  },

  /** Transforms a value that can be either a single item or an array of items into an array. */
  singleOrArray: <T extends zod.ZodType>(schema: T) =>
    zod.union([schema, zod.array(schema)]).transform((value) => {
      if (value === undefined) return undefined;
      return Array.isArray(value) ? value : [value];
    }),

  /** Schema for validating a string is the id of a given object type. */
  awpmarineId: (...types: AwpMarineObjectType[]) =>
    zod.string().refine(
      (value) => {
        if (!AwpMarineId.isValid(value)) return false;
        const parsed = AwpMarineId.parse(value);
        return !types.length || types.includes(parsed.type);
      },
      {
        error: 'Id is not valid or not of the expected types',
      },
    ),
};

const isSensitive = (schema: core.$ZodType) => sensitiveRegistry.get(schema)?.sensitive === true;

// optional, nullable, default, readonly and the other wrappers, plus pipes and lazy schemas
function innerSchema(def: core.$ZodTypeDef): core.$ZodType | undefined {
  if (def.type === 'pipe') return (def as core.$ZodPipeDef).in;
  if (def.type === 'lazy') return (def as core.$ZodLazyDef).getter();
  return 'innerType' in def ? (def.innerType as core.$ZodType) : undefined;
}

/**
 * A copy of `value` with every field `schema` marks sensitive replaced by `[redacted]`. Keys the schema does not know
 * are dropped, as parsing would drop them.
 */
export function redactSensitive(schema: core.$ZodType, value: unknown): unknown {
  if (value === undefined || value === null) return value;
  if (isSensitive(schema)) return '[redacted]';

  const { def } = schema._zod;
  switch (def.type) {
    case 'object': {
      if (typeof value !== 'object' || Array.isArray(value)) return value;
      const { shape } = def as core.$ZodObjectDef;
      return Object.fromEntries(
        Object.entries(value)
          .filter(([key]) => Object.hasOwn(shape, key))
          .map(([key, item]) => [key, redactSensitive(shape[key]!, item)]),
      );
    }
    case 'record': {
      if (typeof value !== 'object' || Array.isArray(value)) return value;
      const { valueType } = def as core.$ZodRecordDef;
      return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, redactSensitive(valueType, item)]));
    }
    case 'array':
      return Array.isArray(value)
        ? value.map((item) => redactSensitive((def as core.$ZodArrayDef).element, item))
        : value;
    case 'union': {
      // the option the value parses as, or the first when it parses as none
      const { options } = def as core.$ZodUnionDef;
      const option = options.find((candidate) => zod.safeParse(candidate, value).success) ?? options[0];
      return option ? redactSensitive(option, value) : value;
    }
    default: {
      const inner = innerSchema(def);
      return inner ? redactSensitive(inner, value) : value;
    }
  }
}

// oxlint-disable no-explicit-any -- any is required here
export namespace z {
  export type infer<T extends zod.ZodType<any, any, any>> = zod.infer<T>;
  export type input<T extends zod.ZodType<any, any, any>> = zod.input<T>;
  export type output<T extends zod.ZodType<any, any, any>> = zod.output<T>;

  // re-export types we use types
  export interface ZodType<
    Output = unknown,
    Input = unknown,
    Internals extends core.$ZodTypeInternals<Output, Input> = core.$ZodTypeInternals<Output, Input>,
  > extends zod.ZodType<Output, Input, Internals> {}
  export interface ZodCoercedString<T = unknown> extends ZodCoercedStringBase<T> {}
  export interface ZodPipe<A extends core.$ZodType, B extends core.$ZodType> extends zod.ZodPipe<A, B> {}
  export interface ZodCodec<Input extends core.$ZodType, Output extends core.$ZodType> extends zod.ZodCodec<
    Input,
    Output
  > {}
}
// oxlint-enable no-explicit-any -- any is required here

export type * from 'zod';
